import { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Button } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import moment from "moment"
import { DEFAULT_PROPS, DEFAULT_SLOT_PROPS } from "helpers/date-picker"
import DateRangePickerDay from "./day"

import "./style.scss"

export default function DateRangePicker({
  earliestDate,
  latestDate,
  startDate: initialStartDate,
  endDate: initialEndDate,
  cancel,
  setDates,
  isDayBlocked,
  isMonthBlocked,
  isYearBlocked,
}) {
  // Because momentjs has no referential integrity, we need to memoize the
  // moment objects before sending them to the material component
  const [minDate, maxDate] = useMemo(
    () => [moment(earliestDate), moment(latestDate)],
    [earliestDate, latestDate],
  )

  const commonProps = {
    ...DEFAULT_PROPS,
    minDate,
    maxDate,
    shouldDisableDate: isDayBlocked,
    shouldDisableMonth: isMonthBlocked,
    shouldDisableYear: isYearBlocked,
    slots: {
      day: DateRangePickerDay,
    },
  }

  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  // We need to manually manage opened state to have the startDate open on initial render
  const [startOpen, setStartOpen] = useState(true)
  const [endOpen, setEndOpen] = useState(false)
  const [startError, setStartError] = useState(null)
  const [endError, setEndError] = useState(null)
  const [startView, setStartView] = useState("day")
  const [endView, setEndView] = useState("day")

  const handleStartFieldChange = (date, { validationError }) => {
    if (!date) {
      validationError = "required"
    }
    // This handles only the change coming from the field component
    setStartDate(date)
    setStartError(validationError)
  }

  const handleStartFieldBlur = () => {
    if (startDate !== null) {
      if (startDate.isAfter(endDate)) {
        setEndDate(startDate)
      }
    }
  }

  const handleSetStartDate = (date, { validationError }) => {
    if (!date) {
      validationError = "required"
    }
    // This handles only the change coming from the calendar component
    setStartDate(date)
    setStartError(validationError)

    // We only switch views if the currently selected view is day
    // so we don't trigger this logic on month and year selection
    if (startView === "day") {
      if (date.isAfter(endDate)) {
        setEndDate(date)
      }
      setEndOpen(true)
    }
  }

  const handleStartOpen = () => {
    setStartOpen(true)
    setEndOpen(false)
  }

  const handleStartViewChange = (view) => {
    // Keeps track of the selected view (year/month/day) for handleSetStartDate logic
    setStartView(view)
  }

  const handleEndFieldChange = (date, { validationError }) => {
    if (!date) {
      validationError = "required"
    }
    // This handles only the change coming from the field component
    setEndDate(date)
    setEndError(validationError)
  }

  const handleEndFieldBlur = () => {
    if (endDate !== null) {
      if (endDate.isBefore(startDate)) {
        setStartDate(endDate)
      }
    }
  }

  const handleSetEndDate = (date, { validationError }) => {
    if (!date) {
      validationError = "required"
    }
    // This handles only the change coming from the calendar component
    setEndDate(date)
    setEndError(validationError)

    // We only switch views if the currently selected view is day
    // so we don't trigger this logic on month and year selection
    if (endView === "day") {
      if (date.isBefore(startDate)) {
        setStartDate(date)
      }
    }
  }

  const handleEndOpen = () => {
    setStartOpen(false)
    setEndOpen(true)
  }

  const handleEndViewChange = (view) => {
    // Keeps track of the selected view (year/month/day) for handleSetEndDate logic
    setEndView(view)
  }

  const handleSetDates = () => {
    // this can happen if you use the year/month picker but don't click on a day
    if (startDate.isAfter(endDate)) {
      setDates(endDate, startDate)
    } else {
      setDates(startDate, endDate)
    }
  }

  return <div className="components_date-range-picker">
    <DatePicker
      {...commonProps}
      className="start-date"
      value={startDate}
      onOpen={handleStartOpen}
      onClose={() => setStartOpen(false)}
      open={startOpen}
      onChange={handleSetStartDate}
      onViewChange={handleStartViewChange}
      slotProps={{
        ...DEFAULT_SLOT_PROPS,
        day: { startDate, endDate },
        textField: {
          size: "small",
          onBlur: handleStartFieldBlur,
        },
        field: {
          onChange: handleStartFieldChange,
        },
        openPickerButton: {
          "data-test-class": "CalendarIcon",
        },
      }}
    />
    <span>to</span>
    <DatePicker
      {...commonProps}
      className="end-date"
      value={endDate}
      onOpen={handleEndOpen}
      onClose={() => setEndOpen(false)}
      open={endOpen}
      onChange={handleSetEndDate}
      onViewChange={handleEndViewChange}
      slotProps={{
        ...DEFAULT_SLOT_PROPS,
        day: { startDate, endDate },
        textField: {
          size: "small",
          onBlur: handleEndFieldBlur,
        },
        field: {
          onChange: handleEndFieldChange,
        },
        openPickerButton: {
          "data-test-class": "CalendarIcon",
        },
      }}
    />
    <Button data-test-class="cancel-date-range-button" onClick={cancel} variant="contained" size="small">
      Cancel
    </Button>
    <Button data-test-class="set-date-range-button" color="primary" variant="contained" size="small" onClick={handleSetDates} disabled={!!startError || !!endError}>
      Set
    </Button>
  </div>
}

DateRangePicker.propTypes = {
  earliestDate: PropTypes.string.isRequired,
  latestDate: PropTypes.string.isRequired,
  startDate: PropTypes.instanceOf(moment).isRequired,
  endDate: PropTypes.instanceOf(moment).isRequired,
  cancel: PropTypes.func.isRequired,
  setDates: PropTypes.func.isRequired,
  isDayBlocked: PropTypes.func.isRequired,
  isMonthBlocked: PropTypes.func,
  isYearBlocked: PropTypes.func,
}
